import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { X } from "lucide-react"
import { type FormEvent, useEffect, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import {
  type Adjuntando,
  AdjuntosNuevos,
  archivosPegados,
  sumar,
} from "@/componentes/ui/AdjuntosNuevos"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"
import { SelectorBuscable } from "@/componentes/ui/SelectorBuscable"
import { esCuentaBloqueada, PinEnLinea } from "./CuentaBloqueada"
import {
  aCentimosRedondeando,
  aTasaMicro,
  centimosDe,
  comoCampo,
  comoTasa,
  entreTasaPreciso,
  motivoImporte,
  motivoTasa,
  notaRedondeo,
  paraRegistrar,
  porTasaPreciso,
  type TasaMicro,
  tasaEntrePreciso,
  tasaRelativa,
  unidadesDe,
} from "./conversion"
import { monto } from "./dinero"
import type { TasaVigente } from "./tasas"
import type { Cuenta, Divisa, Movimiento } from "./tipos"

/** Lo que hace falta de una orden para elegirla y armar el cambio con ella. */
type OrdenBreve = {
  id: number
  codigo: string
  concepto: string
  proveedor: string | null
  divisa: string | null
  /** Lo que de verdad se le transfiere al proveedor: la base menos las retenciones. */
  monto_neto: string
}

const ESTILO = {
  borderColor: "var(--border)",
  backgroundColor: "var(--surface)",
  color: "var(--fg)",
}

/**
 * Registrar un movimiento a mano: un depósito, intereses, una comisión del banco, o
 * una transferencia a otra cuenta de la agencia.
 *
 * ⚠ **Va como dinero de la agencia (`LIQUIDO`).** El otro fondo, `OPERATIVO`, es el
 * anticipo de un cliente para SU expediente, y sólo tiene sentido colgado de uno: un
 * operativo sin expediente sería dinero de nadie para `calcular_saldo_fondo()`. Ése se
 * registra desde el expediente.
 *
 * ⚠ **El monto va en la divisa de la cuenta.** Si no es la de referencia, el servidor
 * congela la tasa vigente; sin tasa registrada responde 409, y aquí se dice por qué.
 *
 * ⚠ **La fecha es la del BANCO, no la de hoy.** Por omisión hoy, y se puede poner
 * una anterior para cargar el extracto de meses pasados. No es antedatar a
 * escondidas: `creado_en` —cuándo se tecleó la fila— sigue ahí y sigue siendo
 * inmutable, así que quedan los dos hechos. Lo que no se admite es una fecha futura:
 * movería el saldo de hoy por algo que el banco no ha hecho.
 *
 * ⚠ **Una transferencia son DOS movimientos, uno en cada extracto**, y los escribe el
 * servidor juntos o ninguno. Aquí sólo se eligen las dos cuentas y los dos importes.
 *
 * ⚠ **Una entrada o una salida exigen su soporte; la transferencia no.** Un movimiento
 * tecleado a mano sin su captura es la palabra de quien lo tecleó contra el extracto
 * del banco. En una transferencia entre cuentas propias el respaldo es la otra mitad
 * de la operación, que ya está en el sistema, así que se admite pero no se pide.
 *
 * ⚠ **El soporte viaja en la MISMA petición que el movimiento** (regla 7): se valida
 * antes de escribir nada, así que un archivo rechazado no deja ni el movimiento.
 *
 * ⚠ **Entre monedas distintas son TRES campos atados: lo que sale, la tasa y lo que
 * entra.** Tocar uno recalcula el que corresponde, y no hay dos casillas diciendo lo
 * mismo con números distintos — que es lo que pasaba con un conversor aparte al lado
 * de los importes: el mismo dinero salía en dos sitios y no coincidía.
 *
 * ⚠ **Lo que manda son los dos importes; la tasa se DERIVA de ellos.** Es lo que dicen
 * los dos extractos. La del BCV entra como sugerencia y se puede teclear para calcular
 * lo que entra, pero al corregir el importe que llegó, la tasa se recalcula sola.
 */
/** Hoy en la zona del navegador, en `AAAA-MM-DD`: lo que espera un `<input type="date">`. */
function hoy(): string {
  const d = new Date()
  const dos = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`
}

/**
 * Una transferencia ya armada al abrir (19/10): la pide el modal de pago cuando la
 * cuenta pagadora no alcanza, para no salir de él a buscar el dinero. Va a la cuenta
 * que paga, ligada a las órdenes —así el pago puede traer después la comisión con SU
 * tasa— y con lo que falta como lo que ENTRA; lo que sale lo calcula la tasa del BCV.
 * Todo sigue tecleable.
 */
export type TransferenciaPreparada = { destinoId: number; ordenIds: number[]; entra: string }

export function DialogoMovimiento({
  cuenta,
  divisa,
  abierto,
  onCerrar,
  preparar,
}: {
  cuenta: Cuenta
  /** El código de la divisa de la cuenta, para decir en qué va el monto. */
  divisa: string
  abierto: boolean
  onCerrar: () => void
  preparar?: TransferenciaPreparada
}) {
  const clienteQuery = useQueryClient()
  const [tipo, setTipo] = useState<"INGRESO" | "EGRESO" | "TRANSFERENCIA">("INGRESO")
  const [importe, setImporte] = useState("")
  const [concepto, setConcepto] = useState("")
  const [referencia, setReferencia] = useState("")
  const [fecha, setFecha] = useState(hoy())
  const [destinoId, setDestinoId] = useState<number | null>(null)
  const [importeDestino, setImporteDestino] = useState("")
  // La tasa es un campo como los otros dos: se guarda tal cual se escribe. Ver la
  // nota de «los tres campos atados», más abajo.
  const [tasaTexto, setTasaTexto] = useState("")
  const [tasaTocada, setTasaTocada] = useState(false)
  // Los dos últimos campos escritos, el más reciente primero. La tasa del BCV cuenta
  // como escrita al abrir. Ver «por combinación», más abajo.
  const [ultimos, setUltimos] = useState<Campo[]>(["tasa"])
  // Las órdenes que este cambio de recursos financia, si financia alguna. Pueden ser
  // varias (15/10): un solo cambio paga lo de todas, que es UN movimiento en cada
  // extracto. El orden es el de elección; la primera encabeza la referencia.
  const [ordenIds, setOrdenIds] = useState<number[]>([])
  const [soporte, setSoporte] = useState<Adjuntando[]>([])
  const [rechazados, setRechazados] = useState<string[]>([])

  const [preparada, setPreparada] = useState(false)
  // Con `preparar`, nace como transferencia hacia esa cuenta; el resto, cuando lleguen
  // las cuentas, las tasas y las órdenes (efecto de más abajo).
  const destinoPreparado = preparar?.destinoId ?? null
  useEffect(() => {
    if (!abierto || destinoPreparado === null) return
    setTipo("TRANSFERENCIA")
    setDestinoId(destinoPreparado)
    setPreparada(false)
  }, [abierto, destinoPreparado])

  const esTransferencia = tipo === "TRANSFERENCIA"

  // Las demás cuentas activas, que son los destinos posibles.
  const cuentas = useQuery({
    queryKey: ["cuentas-bancarias"],
    queryFn: () => api.get<Cuenta[]>("/api/v1/cuentas-bancarias"),
    enabled: abierto && esTransferencia,
  })
  const tasas = useQuery({
    queryKey: ["tasas-vigentes"],
    queryFn: () => api.get<TasaVigente[]>("/api/v1/tasas/vigentes"),
    enabled: abierto && esTransferencia,
  })
  const destinos = (cuentas.data ?? []).filter((c) => c.activo && c.id !== cuenta.id)
  const destino = destinos.find((c) => c.id === destinoId) ?? null
  const otraMoneda = destino !== null && destino.divisa_id !== cuenta.divisa_id
  // El código de la divisa del destino: las cuentas traen el id y hay que resolverlo.
  const divisas = useQuery({
    queryKey: ["divisas"],
    queryFn: () => api.get<Divisa[]>("/api/v1/divisas"),
    enabled: abierto && esTransferencia,
  })
  const divisaDestino = (divisas.data ?? []).find((d) => d.id === destino?.divisa_id)?.codigo ?? ""

  // ⚠ **Sólo las APROBADAS**: son las que ya se pueden pagar, y por tanto las que
  // justifican mover dinero de una cuenta a otra hoy. Una en aprobación todavía puede
  // cambiar de importe, y el servidor sigue admitiéndola por si hace falta.
  const ordenes = useQuery({
    queryKey: ["ordenes-pago", "aprobadas"],
    queryFn: () => api.get<OrdenBreve[]>("/api/v1/ordenes-pago?estado=APROBADA&limit=100"),
    enabled: abierto && esTransferencia,
  })
  const elegidas = ordenIds
    .map((id) => (ordenes.data ?? []).find((o) => o.id === id))
    .filter((o): o is OrdenBreve => o !== undefined)

  // --- Los tres campos atados de la transferencia con cambio -----------------
  //
  // ⚠ **La tasa va SIEMPRE en la lectura natural**: «por 1» la moneda que más vale
  // —«BS por 1 USDT»—, nunca al revés. Con la unidad pequeña como denominador el
  // número sale 0,001052 y seis decimales no bastan para volver sin perder céntimos.
  const bsDe = (codigo: string) => {
    const fila = (tasas.data ?? []).find((f) => f.codigo === codigo)
    return fila?.bs_por_unidad ? aTasaMicro(fila.bs_por_unidad) : null
  }
  const bsOrigen = bsDe(divisa)
  const bsDestino = bsDe(divisaDestino)
  const origenAbajo = bsOrigen !== null && bsDestino !== null ? bsOrigen >= bsDestino : true
  const denominador = origenAbajo ? divisa : divisaDestino
  const numerador = origenAbajo ? divisaDestino : divisa
  const sugerida =
    bsOrigen === null || bsDestino === null
      ? null
      : origenAbajo
        ? tasaRelativa(bsOrigen, bsDestino)
        : tasaRelativa(bsDestino, bsOrigen)

  // La tasa nace en la del BCV del par elegido; desde que alguien la toca —o la
  // recalcula un importe—, es de quien escribe y no se vuelve a pisar.
  useEffect(() => {
    if (!tasaTocada && otraMoneda && sugerida !== null) setTasaTexto(comoTasa(sugerida))
  }, [tasaTocada, otraMoneda, sugerida])

  /** Lo que entra, en céntimos, a partir de lo que sale y de la tasa. */
  const loQueEntra = (sale: bigint, tasa: TasaMicro) =>
    origenAbajo ? porTasaPreciso(sale, tasa) : entreTasaPreciso(sale, tasa)

  /** Y la vuelta: lo que hay que sacar para que entre eso. */
  const loQueSale = (entra: bigint, tasa: TasaMicro) =>
    origenAbajo ? entreTasaPreciso(entra, tasa) : porTasaPreciso(entra, tasa)

  /** La tasa que explica los dos importes, en la lectura natural. */
  const tasaDe = (sale: bigint, entra: bigint) =>
    origenAbajo ? tasaEntrePreciso(entra, sale) : tasaEntrePreciso(sale, entra)

  /**
   * Al elegir o quitar una orden, el cambio se arma solo: entra lo que cuestan JUNTAS
   * —las de la moneda del destino— y sale lo que la tasa diga que hace falta. Cuenta
   * como si se hubiera tecleado lo que entra.
   *
   * ⚠ **Se rellena, no se congela.** Los tres campos siguen siendo tecleables: esto
   * ahorra la cuenta a mano del caso de todos los días, no decide por nadie.
   */
  function cambiarOrdenes(ids: number[]) {
    setOrdenIds(ids)
    const lista = ids
      .map((id) => (ordenes.data ?? []).find((o) => o.id === id))
      .filter((o): o is OrdenBreve => o !== undefined)
    if (lista.length === 0) return
    // El concepto: el de la orden si es una; si son varias, a quién se paga. El
    // servidor le antepone «Cambio de Recursos» y los códigos.
    setConcepto(
      lista.length === 1
        ? (lista[0]?.concepto ?? "")
        : [...new Set(lista.map((o) => o.proveedor ?? o.concepto))].join(", ").slice(0, 150),
    )
    const enDestino = lista.filter((o) => o.divisa === divisaDestino)
    if (enDestino.length === 0) return
    let cuesta = 0n
    for (const o of enDestino) {
      const c = centimosDe(o.monto_neto)
      if (c === null) return
      cuesta += c
    }
    if (!otraMoneda) {
      setImporteDestino(comoCampo(cuesta))
      setImporte(comoCampo(cuesta))
      return
    }
    escribir("entra", comoCampo(cuesta))
  }

  // La transferencia preparada se rellena UNA vez, cuando ya se sabe si el destino es
  // de otra moneda y cuál es la tasa del BCV: entra lo que falta, sale lo que la tasa diga.
  // biome-ignore lint/correctness/useExhaustiveDependencies: se rellena una sola vez, con los datos ya cargados
  useEffect(() => {
    if (!abierto || preparar === undefined || preparada) return
    if (destino === null || ordenes.data === undefined || divisas.data === undefined) return
    if (otraMoneda && sugerida === null) return
    setPreparada(true)
    cambiarOrdenes(preparar.ordenIds)
    const entra = unidadesDe(preparar.entra)
    if (entra === null) return
    // `unidadesDe` da cien milésimas; los campos, céntimos.
    setImporteDestino(comoCampo(aCentimosRedondeando(entra)))
    if (!otraMoneda || sugerida === null) {
      setImporte(comoCampo(aCentimosRedondeando(entra)))
      return
    }
    setTasaTexto(comoTasa(sugerida))
    setImporte(comoCampo(loQueSale(entra, sugerida)))
    setUltimos(["entra", "tasa"])
  }, [abierto, preparar, preparada, destino, ordenes.data, divisas.data, otraMoneda, sugerida])

  /** Lo que suman las elegidas, por moneda: pueden no ser todas de la misma. */
  const totalElegidas = Object.entries(
    elegidas.reduce<Record<string, bigint>>((acc, o) => {
      const clave = o.divisa ?? ""
      acc[clave] = (acc[clave] ?? 0n) + (centimosDe(o.monto_neto) ?? 0n)
      return acc
    }, {}),
  )

  // ⚠ **Los tres campos atados trabajan POR COMBINACIÓN** (lo pidió el usuario el
  // 07/10): el tercero sale SIEMPRE de los dos últimos que se escribieron.
  //
  //   tasa + entra  → se calcula lo que SALE
  //   sale + tasa   → se calcula lo que ENTRA
  //   sale + entra  → se calcula la TASA
  //
  // Antes cada campo tenía su «víctima» fija —la tasa recalculaba siempre lo que
  // entra—, y teclear la tasa después de los bolívares pisaba los bolívares recién
  // escritos con una cuenta que nadie pidió: un cálculo huérfano. Con la combinación,
  // lo que uno escribió nunca se reescribe mientras siga entre los dos últimos.
  //
  // La del BCV cuenta como escrita al abrir: con sólo teclear lo que sale, ya se sabe
  // lo que entra. Y ningún campo se pisa al perder el foco.

  /** El campo que se calcula: el que no está entre los dos últimos escritos. */
  const calculado: Campo | null =
    ultimos.length === 2 ? (CAMPOS.find((c) => !ultimos.includes(c)) ?? null) : null

  function escribir(campo: Campo, texto: string) {
    const siguientes = [campo, ...ultimos.filter((c) => c !== campo)].slice(0, 2)
    setUltimos(siguientes)
    const valores = {
      sale: campo === "sale" ? texto : importe,
      tasa: campo === "tasa" ? texto : tasaTexto,
      entra: campo === "entra" ? texto : importeDestino,
    }
    if (campo === "sale") setImporte(texto)
    if (campo === "tasa") {
      setTasaTexto(texto)
      setTasaTocada(true)
    }
    if (campo === "entra") setImporteDestino(texto)
    if (!otraMoneda) return

    const objetivo = CAMPOS.find((c) => !siguientes.includes(c))
    const sale = unidadesDe(valores.sale)
    const tasa = aTasaMicro(valores.tasa)
    const entra = unidadesDe(valores.entra)
    // Si a la combinación le falta un dato válido, no se calcula nada: mejor un campo
    // quieto que una cifra hecha con medio dato.
    if (objetivo === "entra" && sale !== null && tasa !== null) {
      setImporteDestino(comoCampo(loQueEntra(sale, tasa)))
    } else if (objetivo === "sale" && entra !== null && tasa !== null) {
      setImporte(comoCampo(loQueSale(entra, tasa)))
    } else if (objetivo === "tasa" && sale !== null && entra !== null) {
      const t = tasaDe(sale, entra)
      if (t !== null) {
        setTasaTexto(comoTasa(t))
        setTasaTocada(true)
      }
    }
  }

  /** Lo que dice el campo calculado debajo, para que se sepa de dónde sale. */
  const deDonde: Record<Campo, string> = {
    sale: "Calculado con la tasa y lo que entra.",
    tasa: "Calculada con lo que sale y lo que entra.",
    entra: "Calculado con lo que sale y la tasa.",
  }

  /** Otra cuenta destino es otro par de monedas: la tasa vuelve a la del BCV. */
  function cambiarDestino(id: number | null) {
    setDestinoId(id)
    setTasaTocada(false)
    setTasaTexto("")
    setUltimos(["tasa"])
  }

  // Coma o punto, con o sin miles: el lector de siempre (`lib/decimales.ts`).
  // Lo que se registra: hasta cinco decimales tecleados, dos guardados.
  const normalizado = paraRegistrar(importe)
  const destinoNormalizado = paraRegistrar(importeDestino)
  const validoImporte = normalizado !== ""
  const valido =
    validoImporte &&
    concepto.trim().length >= 3 &&
    (!esTransferencia || (destinoId !== null && (!otraMoneda || destinoNormalizado !== ""))) &&
    // El soporte sólo es obligatorio fuera de la transferencia.
    (esTransferencia || soporte.length === 1)

  const registrar = useMutation({
    meta: { exito: esTransferencia ? "Transferencia registrada" : "Movimiento registrado" },
    mutationFn: () => {
      // Multiparte: los datos como texto JSON y el archivo al lado, que es como el
      // servidor los recibe juntos en una sola petición.
      const formulario = new FormData()
      formulario.append(
        "datos",
        JSON.stringify(
          esTransferencia
            ? {
                cuenta_origen_id: cuenta.id,
                cuenta_destino_id: destinoId,
                monto_origen: normalizado,
                // Con la misma moneda sobra: entra lo mismo que sale, y el servidor
                // lo rechaza si no coincide.
                monto_destino: otraMoneda ? destinoNormalizado : null,
                concepto: concepto.trim(),
                referencia: referencia.trim() || null,
                fecha_valor: fecha,
                // El vínculo, no sólo el texto: es lo que deja preguntar después qué
                // cambio de recursos financió qué orden.
                orden_ids: ordenIds,
              }
            : {
                cuenta_id: cuenta.id,
                tipo,
                clase_fondo: "LIQUIDO",
                // Como texto: el servidor lo lee como `Decimal`, sin coma flotante.
                monto: normalizado,
                concepto: concepto.trim(),
                referencia: referencia.trim() || null,
                fecha_valor: fecha,
              },
        ),
      )
      if (soporte[0]) formulario.append("soporte", soporte[0].archivo)
      return esTransferencia
        ? api.post("/api/v1/transferencias", formulario)
        : api.post<Movimiento>("/api/v1/transacciones", formulario)
    },
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["movimientos"] })
      clienteQuery.invalidateQueries({ queryKey: ["resumen-cuenta"] })
      setImporte("")
      setImporteDestino("")
      setTasaTexto("")
      setTasaTocada(false)
      setUltimos(["tasa"])
      setFecha(hoy())
      setSoporte([])
      setConcepto("")
      setReferencia("")
      setOrdenIds([])
      onCerrar()
    },
  })

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    if (valido) registrar.mutate()
  }

  return (
    <Modal
      abierto={abierto}
      titulo={esTransferencia ? "Transferir a otra cuenta" : "Registrar un movimiento"}
      descripcion={
        esTransferencia
          ? `Sale de ${cuenta.nombre} y entra en otra cuenta: un movimiento en cada extracto.`
          : `En ${cuenta.nombre}, en ${divisa}. Queda con la fecha y la hora de ahora.`
      }
      onCerrar={onCerrar}
      // ⚠ **La transferencia lleva más**: cuenta destino, las órdenes que paga —con el
      // nombre entero del proveedor— y los tres importes atados. En el ancho chico el
      // contenido se salía del panel (05/11, captura del usuario). Entrada y salida
      // siguen en el chico: son cuatro campos.
      ancho={esTransferencia ? "md" : "sm"}
    >
      {/* ⚠ `minmax(0,1fr)` y no la columna `auto` de un `grid` a secas: con ésta, una
          línea que no se parte —el proveedor de una orden, que va recortado con
          `truncate`— ensanchaba la columna más allá del panel y los campos se
          salían por la derecha. Así la columna mide lo que el panel y lo largo se
          recorta. */}
      <form
        onSubmit={enviar}
        className="grid grid-cols-[minmax(0,1fr)] gap-4"
        onPaste={(evento) => {
          // Pegar texto en «concepto» tiene que seguir funcionando: sólo se
          // intercepta cuando lo que viene son archivos.
          const archivos = archivosPegados(evento.clipboardData, 0)
          if (archivos.length === 0) return
          evento.preventDefault()
          const siguiente = sumar([], archivos.slice(0, 1))
          setSoporte(siguiente.adjuntos)
          setRechazados(siguiente.rechazados)
        }}
      >
        <fieldset className="flex gap-4">
          <legend className="mb-1 block text-xs font-medium uppercase">Sentido</legend>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="m-tipo"
              checked={tipo === "INGRESO"}
              onChange={() => setTipo("INGRESO")}
            />
            Entrada
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="m-tipo"
              checked={tipo === "EGRESO"}
              onChange={() => setTipo("EGRESO")}
            />
            Salida
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="m-tipo"
              checked={esTransferencia}
              onChange={() => setTipo("TRANSFERENCIA")}
            />
            Transferencia
          </label>
        </fieldset>

        {esTransferencia && (
          <div>
            <label htmlFor="m-destino" className="mb-1 block text-xs font-medium uppercase">
              Cuenta destino
            </label>
            <select
              id="m-destino"
              value={destinoId ?? ""}
              onChange={(e) => cambiarDestino(e.target.value ? Number(e.target.value) : null)}
              className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
              style={ESTILO}
            >
              <option value="">Elige la cuenta que recibe</option>
              {destinos.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                </option>
              ))}
            </select>
          </div>
        )}

        {esTransferencia && (
          <div>
            <label htmlFor="m-orden" className="mb-1 block text-xs font-medium uppercase">
              ¿Es para pagar órdenes?
            </label>
            {/* ⚠ Un buscador que AGREGA: elegir una la suma a la lista y lo deja listo
                para la siguiente. Las ya elegidas no se vuelven a ofrecer. */}
            <SelectorBuscable
              id="m-orden"
              opciones={(ordenes.data ?? [])
                .filter((o) => !ordenIds.includes(o.id))
                .map((o) => ({
                  valor: o.id,
                  etiqueta: `${o.codigo} · ${o.proveedor ?? "sin proveedor"}`,
                  detalle: `${monto(o.monto_neto)} ${o.divisa ?? ""} · ${o.concepto}`,
                }))}
              valor={null}
              onCambiar={(id) => {
                if (id !== null) cambiarOrdenes([...ordenIds, id])
              }}
              vacio={ordenIds.length === 0 ? "No, es un cambio sin orden" : "Agregar otra orden…"}
              placeholder="Código, proveedor o concepto…"
            />
            {elegidas.length > 0 && (
              <ul className="mt-2 space-y-1" aria-label="Órdenes que paga este cambio">
                {elegidas.map((o) => (
                  <li
                    key={o.id}
                    className="flex items-center gap-2 rounded-[var(--radius-control)] border px-2 py-1 text-xs"
                    style={{ borderColor: "var(--border)" }}
                  >
                    <span className="min-w-0 flex-1 truncate">
                      <strong className="tabular-nums">{o.codigo}</strong> ·{" "}
                      {o.proveedor ?? "sin proveedor"}
                    </span>
                    <span className="shrink-0 tabular-nums">
                      {monto(o.monto_neto)} {o.divisa ?? ""}
                    </span>
                    <button
                      type="button"
                      onClick={() => cambiarOrdenes(ordenIds.filter((id) => id !== o.id))}
                      aria-label={`Quitar ${o.codigo}`}
                      className="foco-cristal -my-1 grid size-8 shrink-0 place-items-center rounded-[var(--radius-control)]"
                    >
                      <X size={14} aria-hidden={true} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {elegidas.length > 1 && (
              <p className="mt-1 text-xs tabular-nums">
                Total de las {elegidas.length} órdenes:{" "}
                {totalElegidas.map(([d, c]) => `${monto(comoCampo(c))} ${d}`).join(" + ")}
              </p>
            )}
            {elegidas.length > 0 && (
              <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
                Quedará: «Cambio de Recursos {elegidas.map((o) => o.codigo).join(", ")} ·{" "}
                {concepto.trim() || elegidas[0]?.concepto} · {divisa} → {divisaDestino || divisa}».
              </p>
            )}
          </div>
        )}

        <div>
          <label htmlFor="m-monto" className="mb-1 block text-xs font-medium uppercase">
            {esTransferencia ? `Sale de esta cuenta (${divisa})` : `Monto (${divisa})`}
          </label>
          <input
            id="m-monto"
            inputMode="decimal"
            value={importe}
            onChange={(e) => escribir("sale", e.target.value)}
            aria-invalid={motivoImporte(importe) !== null}
            placeholder="1500,00"
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm tabular"
            style={ESTILO}
          />
          <Ayuda
            problema={motivoImporte(importe)}
            nota={juntar(
              otraMoneda && calculado === "sale" ? deDonde.sale : null,
              notaRedondeo(importe),
            )}
            texto="Coma o punto para los decimales (hasta cinco), sin separar los miles."
          />
        </div>

        {esTransferencia && otraMoneda && (
          <>
            <div>
              <label htmlFor="m-tasa" className="mb-1 block text-xs font-medium uppercase">
                Tasa · {numerador} por 1 {denominador}
              </label>
              <input
                id="m-tasa"
                inputMode="decimal"
                value={tasaTexto}
                onChange={(e) => escribir("tasa", e.target.value)}
                aria-invalid={motivoTasa(tasaTexto) !== null}
                className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm tabular"
                style={ESTILO}
              />
              <Ayuda
                problema={motivoTasa(tasaTexto)}
                nota={calculado === "tasa" ? deDonde.tasa : null}
                texto="Empieza en la del BCV y se puede corregir. Recalcula lo que entra; al corregir lo que entró, se recalcula ella."
              />
            </div>
            <div>
              <label htmlFor="m-monto-destino" className="mb-1 block text-xs font-medium uppercase">
                Entra en {destino?.nombre} ({divisaDestino})
              </label>
              <input
                id="m-monto-destino"
                inputMode="decimal"
                value={importeDestino}
                onChange={(e) => escribir("entra", e.target.value)}
                aria-invalid={motivoImporte(importeDestino) !== null}
                className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm tabular"
                style={ESTILO}
              />
              <Ayuda
                problema={motivoImporte(importeDestino)}
                nota={juntar(
                  calculado === "entra" ? deDonde.entra : null,
                  notaRedondeo(importeDestino),
                )}
                texto="Lo que dice el extracto de la cuenta que recibe. Al corregirlo se recalcula la tasa."
              />
            </div>
          </>
        )}

        <div>
          <label htmlFor="m-concepto" className="mb-1 block text-xs font-medium uppercase">
            Concepto
          </label>
          <input
            id="m-concepto"
            value={concepto}
            onChange={(e) => setConcepto(e.target.value)}
            maxLength={200}
            placeholder="Comisión de mantenimiento"
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
            style={ESTILO}
          />
        </div>

        <div>
          <label htmlFor="m-fecha" className="mb-1 block text-xs font-medium uppercase">
            Fecha del movimiento
          </label>
          <input
            id="m-fecha"
            type="date"
            value={fecha}
            max={hoy()}
            onChange={(e) => setFecha(e.target.value)}
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm tabular"
            style={ESTILO}
          />
          <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
            La del banco. Ponla anterior para cargar movimientos de antes.
          </p>
        </div>

        <div>
          <label htmlFor="m-referencia" className="mb-1 block text-xs font-medium uppercase">
            Referencia
          </label>
          <input
            id="m-referencia"
            value={referencia}
            onChange={(e) => setReferencia(e.target.value)}
            maxLength={60}
            placeholder="Opcional: el número que da el banco"
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm tabular"
            style={ESTILO}
          />
        </div>

        <fieldset>
          <legend className="mb-1 block text-xs font-medium uppercase">
            Soporte {esTransferencia ? "(opcional)" : ""}
          </legend>
          <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
            {esTransferencia
              ? "Si lo tienes, cuélgalo: queda en los dos movimientos."
              : "Obligatorio: la captura o el recibo del banco. Arrástralo, pégalo o elígelo."}
          </p>
          <AdjuntosNuevos
            adjuntos={soporte}
            rechazados={rechazados}
            etiqueta="Adjuntar soporte"
            multiple={false}
            onArchivos={(archivos) => {
              const siguiente = sumar([], archivos.slice(0, 1))
              setSoporte(siguiente.adjuntos)
              setRechazados(siguiente.rechazados)
            }}
            onQuitar={() => setSoporte([])}
          />
        </fieldset>

        <PinEnLinea error={registrar.error} onAbierta={() => registrar.reset()} />
        {registrar.isError && !esCuentaBloqueada(registrar.error) && (
          <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
            {registrar.error instanceof ErrorApi ? registrar.error.message : "No se pudo registrar"}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Boton variante="fantasma" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton variante="primario" type="submit" disabled={!valido || registrar.isPending}>
            {registrar.isPending ? "Registrando…" : "Registrar"}
          </Boton>
        </div>
      </form>
    </Modal>
  )
}

/**
 * La línea de ayuda de un campo de importe o de tasa.
 *
 * ⚠ **Un campo que no vale lo DICE.** Antes «297,027» —tres decimales— se descartaba en
 * silencio: no recalculaba nada, la tasa volvía a la del BCV y parecía que el formulario
 * no dejaba editar. Ahora sale el motivo en rojo; y si trae más de dos decimales, cómo
 * queda registrado.
 */
function Ayuda({
  problema,
  texto,
  nota = null,
}: {
  problema: string | null
  texto: string
  nota?: string | null
}) {
  if (problema !== null) {
    return (
      <p role="alert" className="mt-1 text-xs" style={{ color: "var(--destructive)" }}>
        {problema}
      </p>
    )
  }
  return (
    <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
      {nota ?? texto}
    </p>
  )
}

/** Los tres campos atados de una transferencia entre monedas. */
type Campo = "sale" | "tasa" | "entra"
const CAMPOS: readonly Campo[] = ["sale", "tasa", "entra"]

/** Dos notas en una línea; `null` si no hay ninguna. */
function juntar(...notas: (string | null)[]): string | null {
  const hay = notas.filter((n): n is string => n !== null)
  return hay.length === 0 ? null : hay.join(" ")
}
